import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { usePanel } from '../../performance-operations/config';
import { KpiSub, KpiValue, Table } from '../../performance-operations/styles';
import {
  KPI_CODES, fetchSummary, formatNumber, hasValue, metricOf, stageBars, surveyRows,
} from '../config';

// PM analitik P7b — E19 özet: KPI'lar (M63–M69), "Sonuç yayımlandı → görüşme vadesi → gerçekleşti → teyit" aşama çubukları (her
// aşamanın paydası ayrı) ve 3 soruluk anonim süreç deneyimi dağılımı (1–5, olumlu %4–5; yanıt kapsaması M69 daima görünür).
// Anket kişi listesine bağlanmaz; k altı soru "Gizlendi". M64 görüşme teyididir — "sonuç kabul oranı" olarak gösterilmez.
function Kpi({ code, metric, loading, error, onRetry }) {
  const { t, i18n } = useTranslation();
  let value;
  if (metric?.suppressionReason) value = t(`performance.experience.suppressed.${metric.suppressionReason === 'performance.experience.hrOnly' ? 'hrOnly' : 'small'}`);
  else if (!hasValue(metric)) value = t('performance.experience.noData');
  else value = `${formatNumber(metric.value, i18n.language)}${metric.unit === 'percent' ? '%' : ''}`;
  const sub = hasValue(metric) && metric.numerator != null && metric.denominator != null
    ? t('performance.experience.ratio', { num: formatNumber(metric.numerator, i18n.language, 0), den: formatNumber(metric.denominator, i18n.language, 0) })
    : null;
  return (
    <PanelCard panelKey={`kpi-${code}`} title={t(`performance.experience.kpi.${code}`)} span={2} kpi compact loading={loading} error={error} onRetry={onRetry}>
      <KpiValue data-kpi={code}>{value}</KpiValue>
      <KpiSub>{t(`performance.experience.kpiHint.${code}`)}</KpiSub>
      {sub ? <KpiSub>{sub}</KpiSub> : null}
    </PanelCard>
  );
}

function StagePanel({ summary, loading, error, onRetry }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const bars = stageBars(summary?.stages).map((b) => ({ ...b, label: t(`performance.experience.stage.${b.key}`) }));
  return (
    <PanelCard
      panelKey="stages" title={t('performance.experience.panel.stages')} span={6}
      loading={loading} error={error} onRetry={onRetry} empty={!summary || summary.stages.published === 0}
      emptyText={t('performance.experience.panel.noPublished')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead><tr><th>{t('performance.experience.col.stage')}</th><th>{t('performance.experience.col.count')}</th><th>{t('performance.experience.col.denominator')}</th></tr></thead>
          <tbody>
            {bars.map((b) => (
              <tr key={b.key} data-stage={b.key}>
                <td>{b.label}</td>
                <td>{formatNumber(b.value, i18n.language, 0)}</td>
                <td>{formatNumber(b.denominator, i18n.language, 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface.border} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: theme.colors.text.muted }} />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11, fill: theme.colors.text.primary }} />
            <Tooltip formatter={(value, name, item) => [
              t('performance.experience.ratio', { num: value, den: item?.payload?.denominator ?? '-' }), t('performance.experience.col.count'),
            ]}
            />
            <Bar dataKey="value" fill={theme.colors.primary.main} radius={[0, 4, 4, 0]} barSize={18} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {summary ? (
        <KpiSub data-note="independent">
          {t('performance.experience.panel.confirmedWithoutMeeting', { count: summary.stages.confirmedWithoutMeeting })}
        </KpiSub>
      ) : null}
    </PanelCard>
  );
}

function SurveyPanel({ summary, loading, error, onRetry }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const palette = [theme.colors.auxiliar.danger, theme.colors.auxiliar.warning, theme.colors.text.light.medium, theme.colors.secondary.main, theme.colors.primary.main];
  const rows = surveyRows(summary?.questions).map((r) => ({ ...r, label: t(`performance.experience.question.${r.key}.short`) }));
  const m69 = metricOf(summary?.metrics, 'M69');
  const unavailable = summary?.surveyUnavailableReason;
  return (
    <PanelCard
      panelKey="survey" title={t('performance.experience.panel.survey')} span={6}
      loading={loading} error={error} onRetry={onRetry}
      empty={!!summary && !unavailable && rows.every((r) => !r.validN && !r.suppressed)}
      emptyText={t('performance.experience.panel.noAnswers')}
      tableToggle={!unavailable} showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <KpiSub data-coverage="M69">
        {t('performance.experience.panel.coverage', {
          value: hasValue(m69) ? `${formatNumber(m69.value, i18n.language)}%` : t('performance.experience.noData'),
          num: summary?.respondents ?? 0, den: summary?.invited ?? 0,
        })}
      </KpiSub>
      {unavailable ? (
        <KpiSub data-state="survey-unavailable">{t(unavailable)}</KpiSub>
      ) : table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.experience.col.question')}</th>
              {[1, 2, 3, 4, 5].map((v) => <th key={v}>{v}</th>)}
              <th>{t('performance.experience.col.validN')}</th>
              <th>{t('performance.experience.col.positivePct')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-question={r.key}>
                <td>{r.label}</td>
                {[1, 2, 3, 4, 5].map((v) => <td key={v}>{r.suppressed ? '-' : formatNumber(r[`s${v}`], i18n.language, 0)}</td>)}
                <td>{r.suppressed ? t('performance.experience.suppressed.small') : formatNumber(r.validN, i18n.language, 0)}</td>
                <td>{r.suppressed || r.positivePct == null ? '-' : `${formatNumber(r.positivePct, i18n.language)}%`}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={rows.filter((r) => !r.suppressed)} layout="vertical" stackOffset="expand" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11, fill: theme.colors.text.primary }} />
            <Tooltip />
            <Legend />
            {[1, 2, 3, 4, 5].map((v, i) => (
              <Bar key={v} dataKey={`s${v}`} stackId="a" name={t(`performance.experience.scale.${v}`)} fill={palette[i]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows.some((r) => r.suppressed) && !unavailable ? (
        <KpiSub data-note="suppressed">{t('performance.experience.panel.suppressedNote', { k: summary?.threshold ?? '' })}</KpiSub>
      ) : null}
    </PanelCard>
  );
}

export default function SummaryPanels({ filters, onMeta }) {
  const filterKey = JSON.stringify(filters);
  const summary = usePanel(() => fetchSummary(filters), [filterKey], !!filters.periodId);
  useEffect(() => {
    if (summary.data) onMeta?.({ calculatedAt: summary.data.calculatedAt, stale: false });
  }, [summary.data, onMeta]);
  const common = { loading: summary.loading, error: summary.error, onRetry: summary.retry };
  return (
    <>
      {KPI_CODES.filter((code) => code !== 'M65' || !summary.data || summary.data.isHr).map((code) => (
        <Kpi key={code} code={code} metric={metricOf(summary.data?.metrics, code)} {...common} />
      ))}
      <StagePanel summary={summary.data} {...common} />
      <SurveyPanel summary={summary.data} {...common} />
    </>
  );
}
